import React, { useState } from 'react';
import { useTranslate, useRedirect } from 'react-admin';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Box,
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { User } from '../types';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

interface UserActionsProps {
    record: User;
    small?: boolean;
}

const UserActions: React.FC<UserActionsProps> = ({ record, small = false }) => {
    const translate = useTranslate();
    const redirect = useRedirect();
    const permissions = useUserPermissions(record);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        redirect(`/users/${record.id}`);
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        setShowDeleteDialog(true);
    };

    const handleDeleteClose = () => {
        setShowDeleteDialog(false);
    };

    // Si la prop `small` es true, mostramos los iconos directamente
    if (small) {
        return (
            <Box sx={{ display: 'flex', gap: 0 }}>
                {permissions.canEditThisUser && (
                    <Tooltip title={translate('ra.action.edit')}>
                        <IconButton size="small" onClick={handleEdit}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {permissions.canDeleteThisUser && (
                    <Tooltip title={translate('ra.action.delete')}>
                        <IconButton size="small" onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {showDeleteDialog && (
                    <BaseDeleteDialog
                        open={showDeleteDialog}
                        onClose={handleDeleteClose}
                        resource="users"
                        recordId={record.id}
                        title={translate('resources.users.dialogs.delete_title', 'Eliminar Usuario')}
                        message={translate('resources.users.dialogs.delete_message', 'Are you sure you want to delete user "{{name}}"?').replace('{{name}}', record.name)}
                    />
                )}
            </Box>
        );
    }

    // Por defecto, mostramos el menú de "más acciones"
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={translate('ra.action.more', 'Más acciones')}>
                    <IconButton onClick={handleMenuOpen} size="small"  sx={{ color: 'action.active' }}>
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {permissions.canEditThisUser && (
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {translate('ra.action.edit', 'Editar')}
                        </ListItemText>
                    </MenuItem>
                )}

                {permissions.canDeleteThisUser && (
                    <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>
                            {translate('ra.action.delete', 'Eliminar')}
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {showDeleteDialog && (
                <BaseDeleteDialog
                    open={showDeleteDialog}
                    onClose={handleDeleteClose}
                    resource="users"
                    recordId={record.id}
                    title={translate('resources.users.dialogs.delete_title', 'Eliminar Usuario')}
                    message={translate('resources.users.dialogs.delete_message', 'Are you sure you want to delete user "{{name}}"?').replace('{{name}}', record.name)}
                />
            )}
        </>
    );
};

export default UserActions;